/**
 * Admin Dashboard Page
 *
 * Platform dashboard (/admin/platform): tabs for system overview, user
 * management, profile moderation, and reports. Opened only with the server's
 * `platform_dashboard` admin function; the moderation buttons show only with
 * its `moderate` action (admin:write) — reading the dashboard does not imply
 * moderating from it. The server enforces both regardless.
 */

import { useState } from 'react';
import { useGetCapabilitiesQuery } from '../../store/api/capabilitiesApi';
import { adminFunctionsFrom } from '../../components/routing/adminAccess';
import {
  useGetAdminOverviewQuery,
  useGetAdminUsersQuery,
  useGetAdminPublicProfilesQuery,
  useHidePublicProfileMutation,
  useUnhidePublicProfileMutation,
  useGetAdminReportsQuery,
  useResolveReportMutation,
  useDismissReportMutation,
  useGetAnalyticsSummaryQuery,
  useGetAnalyticsEventsQuery,
  type AdminUserListItem,
  type AdminPublicProfileItem,
  type ReportDto,
  type ReportStatus,
  type AnalyticsEventType,
} from '../../store/api/adminApi';

type TabId = 'overview' | 'users' | 'profiles' | 'reports' | 'analytics';

interface Tab {
  id: TabId;
  label: string;
}

const tabs: Tab[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'users', label: 'Users' },
  { id: 'profiles', label: 'Public Profiles' },
  { id: 'reports', label: 'Reports' },
  { id: 'analytics', label: 'Analytics' },
];

export function AdminDashboardPage() {
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const { data: caps } = useGetCapabilitiesQuery();
  const canModerate =
    adminFunctionsFrom(caps).find((f) => f.key === 'platform_dashboard')?.actions.includes('moderate') === true;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Platform dashboard</h1>
        <p className="mt-1 text-gray-500">
          Users, content moderation and reports across every workspace
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab content */}
      <div>
        {activeTab === 'overview' && <OverviewTab />}
        {activeTab === 'users' && <UsersTab />}
        {activeTab === 'profiles' && <ProfilesTab canModerate={canModerate} />}
        {activeTab === 'reports' && <ReportsTab canModerate={canModerate} />}
        {activeTab === 'analytics' && <AnalyticsTab />}
      </div>
    </div>
  );
}

// ============================================
// Overview Tab
// ============================================

function OverviewTab() {
  const { data: overview, isLoading, error } = useGetAdminOverviewQuery();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <ErrorMessage message="Failed to load overview data" />;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <StatCard
        title="Total Users"
        value={overview?.userCount ?? 0}
        icon={<UsersIcon />}
        color="blue"
      />
      <StatCard
        title="Public Profiles"
        value={overview?.publicProfileCount ?? 0}
        icon={<ProfileIcon />}
        color="green"
      />
      <StatCard
        title="Marketplace Listings"
        value={overview?.marketplaceCount ?? 0}
        icon={<MarketplaceIcon />}
        color="purple"
      />
    </div>
  );
}

function StatCard({
  title,
  value,
  icon,
  color,
}: {
  title: string;
  value: number;
  icon: React.ReactNode;
  color: 'blue' | 'green' | 'purple' | 'red';
}) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    purple: 'bg-purple-50 text-purple-600',
    red: 'bg-red-50 text-red-600',
  };

  return (
    <div className="card">
      <div className="card-body">
        <div className="flex items-center gap-4">
          <div className={`p-3 rounded-lg ${colorClasses[color]}`}>{icon}</div>
          <div>
            <p className="text-sm text-gray-500">{title}</p>
            <p className="text-2xl font-bold text-gray-900">{value}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================
// Users Tab
// ============================================

function UsersTab() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const { data, isLoading, error } = useGetAdminUsersQuery({
    page,
    pageSize: 20,
    q: search || undefined,
  });

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <ErrorMessage message="Failed to load users" />;
  }

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="max-w-md">
        <input
          type="text"
          placeholder="Search users by name or email..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        />
      </div>

      {/* Users table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Profile
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Joined
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data?.items.map((user) => (
                <UserRow key={user.id} user={user} />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {data && data.totalPages > 1 && (
        <Pagination
          page={page}
          totalPages={data.totalPages}
          onPageChange={setPage}
        />
      )}
    </div>
  );
}

function UserRow({ user }: { user: AdminUserListItem }) {
  return (
    <tr>
      <td className="px-6 py-4 whitespace-nowrap">
        <div>
          <div className="text-sm font-medium text-gray-900">{user.fullName}</div>
          <div className="text-sm text-gray-500">{user.email}</div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span
          className={`px-2 py-1 rounded-full text-xs font-medium ${
            user.role === 'admin'
              ? 'bg-purple-100 text-purple-800'
              : 'bg-gray-100 text-gray-800'
          }`}
        >
          {user.role}
        </span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {user.publicProfileSlug ? (
          <a
            href={`/u/${user.publicProfileSlug}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary-600 hover:underline"
          >
            @{user.publicProfileSlug}
          </a>
        ) : (
          <span className="text-gray-400">—</span>
        )}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {new Date(user.createdAt).toLocaleDateString()}
      </td>
    </tr>
  );
}

// ============================================
// Public Profiles Tab
// ============================================

function ProfilesTab({ canModerate }: { canModerate: boolean }) {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [onlyHidden, setOnlyHidden] = useState(false);

  const { data, isLoading, error } = useGetAdminPublicProfilesQuery({
    page,
    pageSize: 20,
    q: search || undefined,
    onlyHidden,
  });

  const [hideProfile] = useHidePublicProfileMutation();
  const [unhideProfile] = useUnhidePublicProfileMutation();

  const handleToggleHide = async (profile: AdminPublicProfileItem) => {
    try {
      if (profile.isHidden) {
        await unhideProfile(profile.id).unwrap();
      } else {
        await hideProfile(profile.id).unwrap();
      }
    } catch (error) {
      console.error('Failed to toggle profile visibility:', error);
    }
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (isNotAvailable(error)) {
    return <NotAvailableNotice what="Profile moderation" />;
  }

  if (error) {
    return <ErrorMessage message="Failed to load profiles" />;
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 max-w-md">
          <input
            type="text"
            placeholder="Search profiles..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={onlyHidden}
            onChange={(e) => {
              setOnlyHidden(e.target.checked);
              setPage(1);
            }}
            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <span className="text-sm text-gray-700">Only hidden</span>
        </label>
      </div>

      {/* Profiles table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Profile
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Hireable
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data?.items.map((profile) => (
                <ProfileRow
                  key={profile.id}
                  profile={profile}
                  canModerate={canModerate}
                  onToggleHide={() => handleToggleHide(profile)}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {data && data.totalPages > 1 && (
        <Pagination
          page={page}
          totalPages={data.totalPages}
          onPageChange={setPage}
        />
      )}
    </div>
  );
}

function ProfileRow({
  profile,
  canModerate,
  onToggleHide,
}: {
  profile: AdminPublicProfileItem;
  canModerate: boolean;
  onToggleHide: () => void;
}) {
  return (
    <tr className={profile.isHidden ? 'bg-red-50' : ''}>
      <td className="px-6 py-4 whitespace-nowrap">
        <div>
          <div className="text-sm font-medium text-gray-900">
            {profile.displayName}
          </div>
          <a
            href={`/u/${profile.slug}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-primary-600 hover:underline"
          >
            @{profile.slug}
          </a>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex gap-2">
          {profile.isHidden && (
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
              Hidden
            </span>
          )}
          {profile.isPublic && !profile.isHidden && (
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
              Public
            </span>
          )}
          {!profile.isPublic && (
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
              Private
            </span>
          )}
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {profile.isHireable ? (
          <span className="text-green-600">Yes</span>
        ) : (
          <span className="text-gray-400">No</span>
        )}
        {profile.hourlyRate && (
          <span className="ml-2 text-gray-400">
            (${profile.hourlyRate}/hr)
          </span>
        )}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {new Date(profile.createdAt).toLocaleDateString()}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
        {canModerate && <button
          onClick={onToggleHide}
          className={`px-3 py-1 rounded text-sm font-medium ${
            profile.isHidden
              ? 'bg-green-100 text-green-700 hover:bg-green-200'
              : 'bg-red-100 text-red-700 hover:bg-red-200'
          }`}
        >
          {profile.isHidden ? 'Unhide' : 'Hide'}
        </button>}
      </td>
    </tr>
  );
}

// ============================================
// Reports Tab
// ============================================

function ReportsTab({ canModerate }: { canModerate: boolean }) {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<ReportStatus | ''>('');

  const { data, isLoading, error } = useGetAdminReportsQuery({
    page,
    pageSize: 20,
    status: statusFilter || undefined,
  });

  const [resolveReport] = useResolveReportMutation();
  const [dismissReport] = useDismissReportMutation();

  const handleResolve = async (id: string) => {
    try {
      await resolveReport(id).unwrap();
    } catch (error) {
      console.error('Failed to resolve report:', error);
    }
  };

  const handleDismiss = async (id: string) => {
    try {
      await dismissReport(id).unwrap();
    } catch (error) {
      console.error('Failed to dismiss report:', error);
    }
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (isNotAvailable(error)) {
    return <NotAvailableNotice what="Content reports" />;
  }

  if (error) {
    return <ErrorMessage message="Failed to load reports" />;
  }

  return (
    <div className="space-y-4">
      {/* Stats banner */}
      {data && data.openCount > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <WarningIcon className="w-5 h-5 text-yellow-600" />
            <span className="text-yellow-800 font-medium">
              {data.openCount} open report{data.openCount !== 1 ? 's' : ''} requiring review
            </span>
          </div>
        </div>
      )}

      {/* Filter */}
      <div className="max-w-xs">
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value as ReportStatus | '');
            setPage(1);
          }}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="">All statuses</option>
          <option value="open">Open</option>
          <option value="resolved">Resolved</option>
          <option value="dismissed">Dismissed</option>
        </select>
      </div>

      {/* Reports list */}
      {data?.items.length === 0 ? (
        <div className="card">
          <div className="card-body text-center py-12">
            <p className="text-gray-500">No reports found</p>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {data?.items.map((report) => (
            <ReportCard
              key={report.id}
              report={report}
              canModerate={canModerate}
              onResolve={() => handleResolve(report.id)}
              onDismiss={() => handleDismiss(report.id)}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {data && data.totalPages > 1 && (
        <Pagination
          page={page}
          totalPages={data.totalPages}
          onPageChange={setPage}
        />
      )}
    </div>
  );
}

function ReportCard({
  report,
  canModerate,
  onResolve,
  onDismiss,
}: {
  report: ReportDto;
  canModerate: boolean;
  onResolve: () => void;
  onDismiss: () => void;
}) {
  const statusColors = {
    open: 'bg-yellow-100 text-yellow-800',
    resolved: 'bg-green-100 text-green-800',
    dismissed: 'bg-gray-100 text-gray-800',
  };

  const targetLabels: Record<string, string> = {
    profile: 'Profile',
    session: 'Session',
    pitch: 'Pitch',
    expert: 'Expert',
  };

  return (
    <div className="card">
      <div className="card-body">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <span
                className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[report.status]}`}
              >
                {report.status.charAt(0).toUpperCase() + report.status.slice(1)}
              </span>
              <span className="text-sm text-gray-500">
                {targetLabels[report.targetType]} Report
              </span>
            </div>
            <p className="text-gray-900 mb-2">{report.reason}</p>
            <div className="text-sm text-gray-500">
              <span>
                Reported on {new Date(report.createdAt).toLocaleDateString()}
              </span>
              {report.resolvedAt && (
                <span className="ml-4">
                  Resolved on {new Date(report.resolvedAt).toLocaleDateString()}
                </span>
              )}
            </div>
          </div>

          {canModerate && report.status === 'open' && (
            <div className="flex gap-2">
              <button
                onClick={onDismiss}
                className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200"
              >
                Dismiss
              </button>
              <button
                onClick={onResolve}
                className="px-3 py-1.5 text-sm font-medium text-white bg-green-600 rounded hover:bg-green-700"
              >
                Resolve
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================
// Analytics Tab
// ============================================

const EVENT_TYPE_LABELS: Record<AnalyticsEventType, string> = {
  session_started: 'Session Started',
  session_completed: 'Session Completed',
  voice_turn_recorded: 'Voice Turn',
  demo_run: 'Demo Run',
  pitch_listen_snippet: 'Pitch Snippet Listen',
  pitch_listen_full: 'Pitch Full Listen',
  upgrade_cta_clicked: 'Upgrade CTA Clicked',
  plan_checkout_started: 'Plan Checkout Started',
  profile_shared: 'Profile Viewed',
  feedback_rated: 'Feedback Rated',
};

function AnalyticsTab() {
  const { data: summary, isLoading: summaryLoading, error: summaryError } = useGetAnalyticsSummaryQuery();
  const { data: eventsData, isLoading: eventsLoading } = useGetAnalyticsEventsQuery({ limit: 50 });

  if (summaryLoading) {
    return <LoadingSpinner />;
  }

  if (isNotAvailable(summaryError)) {
    return <NotAvailableNotice what="Platform analytics" />;
  }

  if (summaryError) {
    return <ErrorMessage message="Failed to load analytics data" />;
  }

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard
          title="Total Events (24h)"
          value={summary?.totalEvents ?? 0}
          icon={<ChartIcon />}
          color="blue"
        />
        <StatCard
          title="Unique Users"
          value={summary?.uniqueUsers ?? 0}
          icon={<UsersIcon />}
          color="green"
        />
        <StatCard
          title="Feedback Count"
          value={summary?.feedbackCount ?? 0}
          icon={<StarIcon />}
          color="purple"
        />
        <div className="card">
          <div className="card-body">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-yellow-50 text-yellow-600">
                <StarFilledIcon />
              </div>
              <div>
                <p className="text-sm text-gray-500">Avg Rating</p>
                <p className="text-2xl font-bold text-gray-900">
                  {summary?.avgFeedbackRating != null
                    ? summary?.avgFeedbackRating.toFixed(1)
                    : 'N/A'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Events by Type */}
      <div className="card">
        <div className="card-header">
          <h3 className="font-semibold text-gray-900">Events by Type (Last 24h)</h3>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {summary?.eventsByType &&
              (Object.entries(summary.eventsByType) as [AnalyticsEventType, number][])
                .filter(([_, count]) => count > 0)
                .sort((a, b) => b[1] - a[1])
                .map(([type, count]) => (
                  <div key={type} className="text-center p-3 bg-gray-50 rounded-lg">
                    <div className="text-2xl font-bold text-gray-900">{count}</div>
                    <div className="text-xs text-gray-500">{EVENT_TYPE_LABELS[type]}</div>
                  </div>
                ))}
          </div>
        </div>
      </div>

      {/* Top Scenarios */}
      {summary?.topScenarios && summary.topScenarios.length > 0 && (
        <div className="card">
          <div className="card-header">
            <h3 className="font-semibold text-gray-900">Top Scenarios</h3>
          </div>
          <div className="card-body">
            <div className="space-y-2">
              {summary.topScenarios.map((scenario, idx) => (
                <div
                  key={scenario.scenarioKey}
                  className="flex items-center justify-between p-2 bg-gray-50 rounded"
                >
                  <span className="text-sm text-gray-700">
                    {idx + 1}. {scenario.scenarioKey}
                  </span>
                  <span className="text-sm font-medium text-gray-900">
                    {scenario.count} runs
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Recent Events */}
      <div className="card">
        <div className="card-header">
          <h3 className="font-semibold text-gray-900">Recent Events</h3>
        </div>
        <div className="card-body">
          {eventsLoading ? (
            <LoadingSpinner />
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      Time
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      Type
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      User
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      Details
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {eventsData?.events.slice(0, 20).map((event) => (
                    <tr key={event.id}>
                      <td className="px-4 py-2 text-sm text-gray-500 whitespace-nowrap">
                        {new Date(event.createdAt).toLocaleTimeString()}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                        {EVENT_TYPE_LABELS[event.type]}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-500 whitespace-nowrap">
                        {event.userId ? event.userId.slice(0, 8) + '...' : 'Anonymous'}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-500">
                        {Object.entries(event.payload)
                          .filter(([_, v]) => v !== undefined)
                          .map(([k, v]) => `${k}: ${v}`)
                          .join(', ') || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================
// Shared Components
// ============================================

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center py-12">
      <div className="animate-spin w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full" />
    </div>
  );
}

/**
 * The server answers 501 NOT_AVAILABLE for a dashboard section with no backing
 * store yet (moderation, reports, analytics) — shown as such, never as an
 * empty list or zero counts.
 */
export function isNotAvailable(error: unknown): boolean {
  return (error as { status?: unknown } | null | undefined)?.status === 501;
}

export function NotAvailableNotice({ what }: { what: string }) {
  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-gray-700" data-testid="admin-section-not-available">
      {what} is not available yet.
    </div>
  );
}

function ErrorMessage({ message }: { message: string }) {
  return (
    <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
      {message}
    </div>
  );
}

function Pagination({
  page,
  totalPages,
  onPageChange,
}: {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}) {
  return (
    <div className="flex items-center justify-between">
      <button
        onClick={() => onPageChange(page - 1)}
        disabled={page <= 1}
        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Previous
      </button>
      <span className="text-sm text-gray-500">
        Page {page} of {totalPages}
      </span>
      <button
        onClick={() => onPageChange(page + 1)}
        disabled={page >= totalPages}
        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Next
      </button>
    </div>
  );
}

// ============================================
// Icons
// ============================================

function UsersIcon() {
  return (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
      />
    </svg>
  );
}

function ProfileIcon() {
  return (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
      />
    </svg>
  );
}

function MarketplaceIcon() {
  return (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
      />
    </svg>
  );
}

function WarningIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
      />
    </svg>
  );
}

function ChartIcon() {
  return (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
      />
    </svg>
  );
}

function StarIcon() {
  return (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
      />
    </svg>
  );
}

function StarFilledIcon() {
  return (
    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
      <path d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
    </svg>
  );
}
